<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { PlanEditTarget, useBasePriceStore } from "@/modules/base_price/store";
import PlanFillCpmCalendar from "@/modules/base_price/components/PlanFillCpmCalendar.vue";
import PlanMonthBreadcrumb from "@/modules/base_price/components/PlanMonthBreadcrumb.vue";
import PlanMonthLabel from "@/modules/base_price/components/PlanMonthLabel.vue";
import PlanRevenueGauge from "@/modules/base_price/components/PlanRevenueGauge.vue";
import PlanAverageCpmDialog from "@/modules/base_price/components/PlanAverageCpmDialog.vue";
import PlanPatternNudge from "@/modules/base_price/components/PlanPatternNudge.vue";
import PlanPreviousYearsCpmDialog from "@/modules/base_price/components/PlanPreviousYearsCpmDialog.vue";
import PlanPreviousYearsFillDialog from "@/modules/base_price/components/PlanPreviousYearsFillDialog.vue";
import { CPM_COLOR, FILL_COLOR } from "@/modules/base_price/utils/metricColors";
import ResolvedNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedNodeDTO;
import ResolvedAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedAssetDTO;

/**
 * Edits whatever the panel is pointed at — a hierarchy node, or one screen
 * attached to one. The resolver prices both the same way and both carry the
 * same monthly arrays (see ResolvedAssetDTO), so nothing below needs to know
 * which it is; the store decides which column a saved override lands in.
 */
const props = defineProps<{ target: PlanEditTarget }>();

const store = useBasePriceStore();

const MONTHS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

const isReservationBook = computed(
	() => store.selectedBook?.type === "reservation"
);

/**
 * monthly_manual_cpm (unlike every other monthly array on ResolvedNodeDTO)
 * is generated as a keyed object rather than a plain array — a nullable
 * PHP array type doesn't get the same Array<T> inference the non-nullable
 * ones do. Same "normalize a keyed-object-or-array shape" story as every
 * polymorphic Collection elsewhere in this store.
 */
const normalizeMonthlyCpm = (
	value: { [key: number]: number | null } | (number | null)[]
): (number | null)[] => Object.values(value);

/**
 * Per-month CPM for the reservation-book read-only view — derived straight
 * from already-resolved figures (revenue ÷ sellable/1000), no new backend
 * field needed since reservation books never carry a fill/manual-CPM
 * editor at all (reserved inventory is assumed sold; CPM there is purely
 * an outcome of the reservation share, not an input).
 */
const readOnlyMonthlyCpm = computed<number[]>(() =>
	props.target.monthly_revenue.map((revenue, i) => {
		const sellable = props.target.monthly_sellable_impressions[i];
		return sellable > 0 ? revenue / (sellable / 1000) : 0;
	})
);

// --- Draft state (editable, open-inventory book only) ---

/**
 * A true draft, separate from the store's preview overrides — same as
 * Seasonality: commits only on Apply, not per keystroke/drag. Fill and
 * manual CPM are edited together in this one tab but are two independent
 * override types (fill/manual_cpm) — both are always sent together on
 * Apply, same as Seasonality always sends its whole 12-month draft.
 */
const draftFill = reactive<number[]>(Array(12).fill(0));
const draftCpm = reactive<(number | null)[]>(Array(12).fill(null));
const originalFill = reactive<number[]>(Array(12).fill(0));
const originalCpm = reactive<(number | null)[]>(Array(12).fill(null));
const dirty = ref(false);

/**
 * A floor and/or ceiling the price may never leave — see
 * CpmBoundsOverrideDTO. Either end may be empty. They inherit like the rates
 * do, so what is shown is this target's own pair when it has one and its
 * nearest ancestor's otherwise.
 */
const draftMinCpm = ref<number | null>(null);
const draftMaxCpm = ref<number | null>(null);
const originalMinCpm = ref<number | null>(null);
const originalMaxCpm = ref<number | null>(null);

/** Holds a CPM inside the bounds. The single place any price is admitted from the editor. */
const clampCpm = (value: number): number => {
	if (draftMinCpm.value !== null && value < draftMinCpm.value) {
		return draftMinCpm.value;
	}

	if (draftMaxCpm.value !== null && value > draftMaxCpm.value) {
		return draftMaxCpm.value;
	}

	return value;
};

const boundsInvalid = computed(
	() =>
		draftMinCpm.value !== null &&
		draftMaxCpm.value !== null &&
		draftMinCpm.value > draftMaxCpm.value
);

/**
 * A hand-set price already outside the bounds — only reachable by bounding a
 * target whose manual CPM was set earlier, since the editor refuses to create
 * one. The resolver clamps it regardless; this is the disagreement to fix.
 */
const cpmOutOfBounds = computed(() =>
	draftCpm.some(
		(value) => value !== null && value > 0 && clampCpm(value) !== value
	)
);

/**
 * The two switches in the top-right, saved with the rates they produced (see
 * FillOverrideDTO) rather than reset to a default every time the tab opens.
 *
 * `holdBudget` matters most: it decides whether editing one of fill/CPM
 * solves the other to keep revenue on budget — see onFillChange() /
 * onCpmChange() — so reopening a target without it would silently change what
 * the next edit does. `primaryMetric` is only which figure renders as the
 * draggable bars and which as the overlay line; the other is still editable.
 *
 * Declared here, above the seed watch, because that watch is immediate and
 * writes both.
 */
const holdBudget = ref(true);
const primaryMetric = ref<"fill" | "cpm">("cpm");

/** What a target with no fill row of its own opens with — the plan's own default. */
const defaultHoldBudget = () => store.plan?.default_lock !== "free";
const originalHoldBudget = ref(true);
const originalPrimaryMetric = ref<"fill" | "cpm">("cpm");

/**
 * Both switches are saved, so changing one is a real edit and has to enable
 * Apply — binding them straight to the refs left the button disabled and the
 * change lost on the next visit.
 */
const setHoldBudget = (value: boolean) => {
	if (holdBudget.value === value) return;

	holdBudget.value = value;
	dirty.value = true;
};

const setBound = (which: "min" | "max", value: number | null) => {
	const next =
		value === null || Number.isNaN(value) ? null : Math.max(0, value);

	if (which === "min") {
		draftMinCpm.value = next;
	} else {
		draftMaxCpm.value = next;
	}

	// Pull any month the new bound now rejects back inside it, so the editor
	// never holds a price it would refuse to let you type.
	draftCpm.forEach((cpm, i) => {
		if (cpm !== null && cpm > 0) draftCpm[i] = clampCpm(cpm);
	});

	dirty.value = true;
	recomputeAxisMax();
};

const setPrimaryMetric = (value: "fill" | "cpm") => {
	if (primaryMetric.value === value) return;

	primaryMetric.value = value;
	dirty.value = true;
};

/**
 * Manual CPM falls back to derived pricing for months with no override —
 * mirrors BasePriceBookResolver::applyMonthlyRates()'s own derivedCpm
 * formula (budget ÷ sellable/1000) so the chart shows a real representative
 * value for every month, not a misleading 0.
 */
const effectiveCpm = (i: number): number => {
	if (draftCpm[i] !== null) return draftCpm[i]!;
	const sellable =
		props.target.monthly_gross_impressions[i] * (draftFill[i] / 100);
	return sellable > 0 ? props.target.monthly_budget[i] / (sellable / 1000) : 0;
};

/**
 * Axis scale for both the bars and the overlay line — deliberately frozen
 * refs, NOT live computeds off draftFill/draftCpm. If these recomputed on
 * every keystroke/drag frame, editing one month would rescale the whole
 * chart and every OTHER month's bar/line would visibly shift even though
 * its own value never changed (confusing — looked like a real change).
 * recomputeAxisMax() is only called at the end of a discrete edit (drag
 * release, typed input blur, bulk set, seed) — never mid-drag.
 */
const maxFillValue = ref(1.2);
const maxCpmValue = ref(1.2);

const recomputeAxisMax = () => {
	maxFillValue.value = Math.max(...draftFill, 1) * 1.2;
	// The ceiling is drawn as a line, so it has to fit on the axis even when
	// no month reaches it — otherwise setting one appears to do nothing.
	maxCpmValue.value =
		Math.max(
			...MONTHS.map((_, i) => effectiveCpm(i)),
			draftMaxCpm.value ?? 0,
			draftMinCpm.value ?? 0,
			1
		) * 1.2;
};

const seedFrom = (
	fill: number[],
	cpm: (number | null)[],
	hold: boolean = defaultHoldBudget(),
	metric: "fill" | "cpm" = "cpm",
	min: number | null = null,
	max: number | null = null
) => {
	fill.forEach((v, i) => {
		draftFill[i] = Math.round(v * 10) / 10;
		originalFill[i] = draftFill[i];
	});
	cpm.forEach((v, i) => {
		draftCpm[i] = v === null ? null : Math.round(v * 100) / 100;
		originalCpm[i] = draftCpm[i];
	});
	holdBudget.value = hold;
	primaryMetric.value = metric;
	originalHoldBudget.value = hold;
	originalPrimaryMetric.value = metric;
	draftMinCpm.value = min;
	draftMaxCpm.value = max;
	originalMinCpm.value = min;
	originalMaxCpm.value = max;
	dirty.value = false;
	recomputeAxisMax();
};

/**
 * Seeds from the rows that were actually applied, falling back to the
 * resolved values only where this target has none of its own (i.e. it is
 * inheriting) — so leaving a target and coming back restores the
 * configuration rather than the resolver's rendering of it. Also run after
 * the calendar applies a month, since that can save Hold budget and the
 * toggle on the same fill row.
 */
const seedFromDraft = () => {
	const fill = store.targetOverride("fill");
	const cpm = store.targetOverride("manual_cpm");
	const bounds = store.targetOverride("cpm_bounds");

	seedFrom(
		fill?.monthly_fill_rates ?? props.target.monthly_fill_rates,
		normalizeMonthlyCpm(cpm?.monthly_cpm ?? props.target.monthly_manual_cpm),
		fill ? fill.hold !== "free" : defaultHoldBudget(),
		fill?.primary_metric ?? "cpm",
		// Own row if there is one, otherwise whatever this target inherits.
		bounds ? bounds.min_cpm ?? null : props.target.cpm_min,
		bounds ? bounds.max_cpm ?? null : props.target.cpm_max
	);
};

/**
 * The month open in the calendar (1-12), or null for the twelve-month chart.
 * Kept when the selection moves, so stepping through the hierarchy compares
 * the same December everywhere.
 */
const calendarMonth = ref<number | null>(null);

/** The calendar's own reset, surfaced in this toolbar beside the toggles. */
const calendarRef = ref<InstanceType<typeof PlanFillCpmCalendar> | null>(null);

const MONTH_NAMES = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];

/**
 * Months (0-11) this target prices by day itself — months its own day-fill or
 * day-CPM row lists — marked like an overridden node in the hierarchy: own
 * settings only, never inherited ones.
 */
const monthsWithDayPricing = computed<Set<number>>(() => {
	const months = new Set<number>();
	const year = String(store.plan?.year ?? "");

	[store.targetOverride("day_fill"), store.targetOverride("day_manual_cpm")].forEach(
		(row) =>
			row?.months?.forEach((key) => {
				if (key.startsWith(`${year}-`)) months.add(Number(key.slice(5, 7)) - 1);
			})
	);

	return months;
});

/**
 * Opens a month by day. Not while the twelve months have unapplied changes:
 * a day follows its month's fill and price, and those would be figures the
 * chart has already moved away from.
 */
const openMonth = (i: number) => {
	if (dirty.value) return;

	calendarMonth.value = i + 1;
};

// Only re-seeds when the *node or book* actually changes — not on every
// resolve (e.g. an unrelated target edit elsewhere), which would otherwise
// wipe an in-progress, unsaved fill/CPM edit out from under the user. Same
// pattern as PlanSeasonalityTab.vue.
watch(
	// Keyed on kind too: a node id and an asset id are separate spaces and
	// can collide, and switching between them must re-seed.
	() => [props.target.kind, props.target.id, store.selectedBookUuid],
	() => seedFromDraft(),
	{ immediate: true }
);

/**
 * Solves this month's fill rate backward from a manual CPM so that
 * revenue still lands on this node's own allocated budget for that month
 * (budget is season-driven, never fill-driven — see
 * BasePriceBookResolver::applyMonthlyRates()): sellable_needed =
 * budget ÷ (cpm/1000), fill_needed = sellable_needed ÷ gross × 100.
 */
const solveFillForCpm = (i: number, cpm: number): number => {
	const gross = props.target.monthly_gross_impressions[i];
	const budget = props.target.monthly_budget[i];
	if (gross <= 0 || cpm <= 0) return draftFill[i];

	const sellableNeeded = budget / (cpm / 1000);
	return Math.min(100, Math.max(0, (sellableNeeded / gross) * 100));
};

/**
 * Editing fill while holding the budget clears that month's manual CPM
 * (reverts to derived) — derived CPM is by definition budget ÷ sellable,
 * so falling back to it after a fill change always makes revenue land
 * back on budget automatically, no solving needed. With holding off, fill
 * just changes on its own and revenue is free to drift.
 */
const onFillChange = (i: number, value: number) => {
	draftFill[i] = Math.min(100, Math.max(0, value));
	if (holdBudget.value) draftCpm[i] = null;
	dirty.value = true;
};

/**
 * Editing CPM while holding the budget solves fill backward (see
 * solveFillForCpm()); with holding off, CPM just changes on its own.
 * `value === null` means the input was cleared — reverts that month to
 * derived pricing (not the same as typing 0, a real fixed price of €0).
 */
const onCpmChange = (i: number, value: number | null) => {
	// Bounds are absolute: nothing entered here, dragged, or solved may leave
	// them. Clearing a month (null) is still allowed — that reverts it to the
	// derived price, which the resolver clamps on its own.
	if (value !== null) value = clampCpm(value);

	if (value !== null && holdBudget.value)
		draftFill[i] = solveFillForCpm(i, value);
	draftCpm[i] = value;
	dirty.value = true;
};

const onCpmInput = (i: number, rawText: string) => {
	onCpmChange(i, rawText.trim() === "" ? null : Number(rawText));
	recomputeAxisMax();
};

/** Wraps onFillChange() for the typed input (not the bar drag) — a discrete edit, so the axis is safe to rescale here. */
const onFillInput = (i: number, value: number) => {
	onFillChange(i, value);
	recomputeAxisMax();
};

// --- Assists ---

/** Local-draft-only — clears every month's manual CPM (fill left untouched), reverting all 12 months to derived pricing. Requires Apply, unlike resetToInherited() below, since it's a plain edit, not a revert-to-inherited action. */
/**
 * Where a bound sits on the chart, as a distance from the bottom. Only
 * meaningful while CPM is the metric on the axis — with fill on the bars the
 * axis is a percentage and a euro line would be nonsense, so the lines are
 * hidden rather than mis-placed.
 */
const boundTop = (value: number): string =>
	`${chartHeight - (value / maxCpmValue.value) * chartHeight}px`;

const showBoundLines = computed(() => primaryMetric.value === "cpm");

/** Is this month's price sitting on a bound rather than where the model put it? */
const isAtBound = (i: number): boolean => {
	const cpm = effectiveCpm(i);

	return (
		(draftMinCpm.value !== null && cpm <= draftMinCpm.value) ||
		(draftMaxCpm.value !== null && cpm >= draftMaxCpm.value)
	);
};

/**
 * The bound this month's price would be clamped to, or null when it prices
 * inside them. Deliberately read *after* the hand-set price for the month has
 * been cleared, so what gets tested is the derived price — the one the month
 * would actually be given.
 */
const violatedBound = (i: number): number | null => {
	const derived = effectiveCpm(i);
	if (derived <= 0) return null;

	if (draftMinCpm.value !== null && derived < draftMinCpm.value) {
		return draftMinCpm.value;
	}

	if (draftMaxCpm.value !== null && derived > draftMaxCpm.value) {
		return draftMaxCpm.value;
	}

	return null;
};

/**
 * Clears every hand-set price, so each month falls back to the derived one —
 * budget ÷ sellable, which lands revenue on budget by definition — and then
 * deals with the months a price bound would push straight back off it.
 *
 * A bound is the last word in the resolver: the clamp in
 * BasePriceBookResolver::applyMonthlyRates() runs after the derived and the
 * hand-set price alike, so a floor makes a month earn more than its budget
 * asked for and a ceiling makes it earn less, and clearing prices alone can
 * never fix that. The price itself cannot move — being pinned at the bound is
 * what the bound *means* — which leaves fill as the only lever: sell fewer
 * impressions at the floor, or more at the ceiling.
 *
 * Solving fill to the bound is self-consistent rather than a second override
 * fighting the first: at fill = budget ÷ (bound/1000) ÷ gross the derived
 * price works out to exactly the bound, so the clamp stops biting and nothing
 * has to be hand-set to hold it there.
 *
 * Note this moves fill, which is a demand assumption and not a pricing dial.
 * That is the honest reading of a floor — you would rather sell less than sell
 * below it — but it is a real change to the model, not a rounding fix.
 */
const solveToBudget = () => {
	// No price satisfies a floor above its ceiling, so there is nothing to
	// solve towards; clear the hand-set prices and leave fill alone.
	const solvable = !boundsInvalid.value;

	for (let i = 0; i < 12; i++) {
		draftCpm[i] = null;

		if (!solvable) continue;

		const bound = violatedBound(i);
		// solveFillForCpm() caps at 100, so a ceiling that no amount of
		// inventory can reach lands at full sell-through and a shortfall —
		// see unreachableMonths, which says so rather than pretending.
		if (bound !== null) draftFill[i] = solveFillForCpm(i, bound);
	}

	dirty.value = true;
	recomputeAxisMax();
};

/**
 * Months the ceiling puts out of reach: even selling every impression at the
 * cap earns less than the budget asked for, so no fill solves them. A floor
 * has no equivalent — fill can always come down.
 */
const averageCpmOpen = ref(false);

/**
 * Takes what "Define average CPM" calculated and writes it into the draft as
 * ordinary month values — the average itself is not kept anywhere. Each
 * month gets the price as a hand-set CPM and the fill re-solved for that
 * rounded price, so it lands on its budget to the cent rather than drifting
 * by the rounding.
 */
type AverageCpmMonths = ({ fill: number; cpm: number } | null)[];

/** The draft's months with a calculated result in them — what apply writes, and what evaluate resolves. */
const withAverageCpm = (months: AverageCpmMonths) => {
	const fills = draftFill.slice();
	const cpms = draftCpm.slice();

	months.forEach((month, i) => {
		if (!month) return;

		cpms[i] = Math.round(month.cpm * 100) / 100;
		fills[i] = solveFillForCpm(i, cpms[i]!);
	});

	return { fills, cpms };
};

const applyAverageCpm = (months: AverageCpmMonths) => {
	const { fills, cpms } = withAverageCpm(months);

	fills.forEach((fill, i) => (draftFill[i] = fill));
	cpms.forEach((cpm, i) => (draftCpm[i] = cpm));

	dirty.value = true;
	recomputeAxisMax();
};

/**
 * The year average the plan would really land on with these months applied —
 * resolved against a copy of the draft, the same rows apply() would write.
 */
const evaluateAverageCpm = (months: AverageCpmMonths) => {
	const { fills, cpms } = withAverageCpm(months);

	return store.previewTargetCpm({
		fill: {
			monthly_fill_rates: fills,
			hold: holdBudget.value ? "budget" : "free",
			primary_metric: primaryMetric.value,
		},
		manual_cpm: { monthly_cpm: cpms },
	});
};

const previousYearsCpmOpen = ref(false);

/**
 * Takes the CPMs "Derive CPM from previous years" picked and sets them as
 * hand-set prices, month by month, exactly as if typed — clamped to the
 * bounds, and with Hold budget on the fill re-solved so the month stays on
 * budget (see onCpmChange()). A month the chosen year has no CPM for is left
 * as it is.
 */
const applyPreviousYearsCpm = (cpms: number[]) => {
	cpms.forEach((cpm, i) => {
		if (cpm > 0) onCpmChange(i, Math.round(cpm * 100) / 100);
	});

	recomputeAxisMax();
};

const previousYearsFillOpen = ref(false);

/**
 * Takes the fill rates "Derive fill rate from previous years" picked and sets
 * them month by month, exactly as if typed — capped at 100%, and with Hold
 * budget on the month's hand-set CPM cleared so it lands back on budget (see
 * onFillChange()), the mirror of applyPreviousYearsCpm(). A month the chosen
 * year has no fill for is left as it is.
 */
const applyPreviousYearsFill = (fills: number[]) => {
	fills.forEach((fill, i) => {
		if (fill > 0) onFillChange(i, Math.round(fill * 10) / 10);
	});

	recomputeAxisMax();
};

const unreachableMonths = computed(() => {
	const max = draftMaxCpm.value;
	if (max === null || max <= 0) return 0;

	return MONTHS.reduce((count, _, i) => {
		const gross = props.target.monthly_gross_impressions[i];
		const budget = props.target.monthly_budget[i];
		if (gross <= 0 || budget <= 0) return count;

		return budget / (max / 1000) > gross ? count + 1 : count;
	}, 0);
});

/**
 * Would Solve to budget change anything? It clears hand-set prices and moves
 * fill where a bound pushes a month off budget, so with neither there is
 * nothing for it to do — every month already prices from its budget.
 */
const solveWouldChange = computed(
	() =>
		draftCpm.some((value) => value !== null) ||
		(!boundsInvalid.value && MONTHS.some((_, i) => violatedBound(i) !== null))
);

const solveHint = computed(() => {
	if (!solveWouldChange.value) {
		return "Nothing to solve: no month has a hand-set price, and every month already earns exactly the budget it was given.";
	}

	return draftMinCpm.value === null && draftMaxCpm.value === null
		? "Clears every hand-set price, so each month earns exactly the budget it was given."
		: "Clears every hand-set price, then moves fill where a bound would otherwise push a month off its budget.";
});

/**
 * This tab writes three override rows — fill, manual CPM and price bounds —
 * but presents them as one configuration, so any of the three being set here
 * offers the reset, and the reset clears all three. Leaving bounds behind made
 * them unresettable: nothing else in the editor clears that row, so a floor
 * set once could only ever be widened, never removed.
 */
const canReset = computed(
	() =>
		(props.target.fill_own ||
			props.target.manual_cpm_own ||
			props.target.cpm_bounds_own) &&
		props.target.inheritsFrom !== null
);

/** Clears all three immediately (bypasses Apply, same as Seasonality's own reset-to-inherited), then seeds instantly from the parent's already-resolved values rather than waiting on the debounced resolve. */
const resetToInherited = () => {
	const inherited = props.target.inheritsFrom;
	if (!inherited) return;

	store.setTargetOverride("fill", null);
	store.setTargetOverride("manual_cpm", null);
	store.setTargetOverride("cpm_bounds", null);

	// What this target falls back to — a node's parent, for a screen the node
	// carrying it, and at the root the book's own defaults — is exactly what
	// it will now inherit, bounds included: cpm_min/cpm_max there are already
	// that target's *effective* pair, so a grandparent's bounds survive the
	// reset exactly as they should, and the root's clear to unbounded.
	seedFrom(
		inherited.monthly_fill_rates,
		normalizeMonthlyCpm(inherited.monthly_manual_cpm),
		defaultHoldBudget(),
		"cpm",
		inherited.cpm_min,
		inherited.cpm_max
	);
};

// --- Save / Cancel ---
const apply = () => {
	if (boundsInvalid.value) return;

	store.setTargetOverride("fill", {
		monthly_fill_rates: draftFill.slice(),
		hold: holdBudget.value ? "budget" : "free",
		primary_metric: primaryMetric.value,
	});
	store.setTargetOverride("manual_cpm", { monthly_cpm: draftCpm.slice() });

	// Clearing both ends removes the row rather than saving a pair of nulls,
	// so an unbounded target carries no bounds override at all and simply
	// inherits again.
	store.setTargetOverride(
		"cpm_bounds",
		draftMinCpm.value === null && draftMaxCpm.value === null
			? null
			: { min_cpm: draftMinCpm.value, max_cpm: draftMaxCpm.value }
	);

	originalFill.forEach((_, i) => (originalFill[i] = draftFill[i]));
	originalCpm.forEach((_, i) => (originalCpm[i] = draftCpm[i]));
	originalHoldBudget.value = holdBudget.value;
	originalPrimaryMetric.value = primaryMetric.value;
	originalMinCpm.value = draftMinCpm.value;
	originalMaxCpm.value = draftMaxCpm.value;
	dirty.value = false;
};

const cancel = () => {
	originalFill.forEach((v, i) => (draftFill[i] = v));
	originalCpm.forEach((v, i) => (draftCpm[i] = v));
	holdBudget.value = originalHoldBudget.value;
	primaryMetric.value = originalPrimaryMetric.value;
	draftMinCpm.value = originalMinCpm.value;
	draftMaxCpm.value = originalMaxCpm.value;
	dirty.value = false;
};

// --- Chart geometry (dual-metric: bars + overlay line) ---
const chartHeight = 160;

// Fixed per-metric colors — these never change; only which metric renders
// as bars vs. line (driven by primaryMetric above) swaps.
const barColor = computed(() =>
	primaryMetric.value === "fill" ? FILL_COLOR : CPM_COLOR
);
const lineColor = computed(() =>
	primaryMetric.value === "fill" ? CPM_COLOR : FILL_COLOR
);

/**
 * This month's expected revenue at the current draft fill/CPM — with "Hold
 * budget" on this always lands back on monthly_budget by construction (see
 * onFillChange()/onCpmChange() above), but with it off fill and CPM move
 * independently, so revenue is free to drift. Mirrors the design's own
 * per-month revenue + delta-vs-budget readout below each CPM box.
 */
const monthRevenue = (i: number): number =>
	(effectiveCpm(i) *
		(props.target.monthly_gross_impressions[i] * (draftFill[i] / 100))) /
	1000;

/** ~0.2% (or €1 on a zero budget) counts as "at budget" — matches the design's own off/on-budget threshold. */
const monthDelta = (i: number) => {
	const budget = props.target.monthly_budget[i];
	const delta = monthRevenue(i) - budget;
	const off =
		budget > 0 ? Math.abs(delta / budget) > 0.002 : Math.abs(delta) > 1;

	return {
		label: off
			? `${delta > 0 ? "+" : ""}${formatNumber(delta, "money")}`
			: "at budget",
		colorClass: !off ? "text-success" : delta < 0 ? "text-error" : "text-info",
	};
};

const fillValuePct = (i: number) => (draftFill[i] / maxFillValue.value) * 100;
const cpmValuePct = (i: number) => (effectiveCpm(i) / maxCpmValue.value) * 100;

const barValuePct = (i: number) =>
	primaryMetric.value === "fill" ? fillValuePct(i) : cpmValuePct(i);
const lineValuePct = (i: number) =>
	primaryMetric.value === "fill" ? cpmValuePct(i) : fillValuePct(i);

const barHeight = (i: number) => (barValuePct(i) / 100) * chartHeight;

const barValueLabel = (i: number) =>
	primaryMetric.value === "fill"
		? `${draftFill[i].toFixed(1)}%`
		: formatNumber(effectiveCpm(i), "money");

/**
 * Overlay line (the metric NOT currently primary) — normalized coordinate
 * space, one unit per month column (0..12), y in 0..100 matching the bars'
 * own percent-of-max scale. Rendered with vector-effect="non-scaling-stroke"
 * so the line stays a crisp fixed width despite the non-uniform x/y scale
 * from preserveAspectRatio="none".
 */
const linePoints = computed(() =>
	MONTHS.map((_, i) => `${i + 0.5},${100 - lineValuePct(i)}`).join(" ")
);

// --- Drag-to-edit (drags whichever metric is currently primary) ---
let dragIndex: number | null = null;
let dragStartY = 0;
let dragStartValue = 0;

const onDragMove = (event: PointerEvent) => {
	if (dragIndex === null) return;
	const maxValue =
		primaryMetric.value === "fill" ? maxFillValue.value : maxCpmValue.value;
	const delta = (dragStartY - event.clientY) / (chartHeight / maxValue);
	if (primaryMetric.value === "fill") {
		onFillChange(dragIndex, dragStartValue + delta);
	} else {
		onCpmChange(dragIndex, Math.max(0, dragStartValue + delta));
	}
};

const onDragEnd = () => {
	dragIndex = null;
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
	recomputeAxisMax();
};

const onBarPointerDown = (i: number, event: PointerEvent) => {
	dragIndex = i;
	dragStartY = event.clientY;
	dragStartValue =
		primaryMetric.value === "fill" ? draftFill[i] : effectiveCpm(i);
	window.addEventListener("pointermove", onDragMove);
	window.addEventListener("pointerup", onDragEnd);
};

// --- Shift the whole pattern ---

/**
 * The slider beside the chart moves every month of the bars' figure — fill or
 * CPM, whichever the toggle shows as bars — up or down by the same amount, so
 * the pattern keeps its shape. It works from the months as they were when it
 * was picked up: every move re-applies its offset to that snapshot rather
 * than adding to the last move, so dragging back to the middle puts the
 * months back exactly as they were. It springs back to 0 on release, ready to
 * nudge again from the new pattern.
 *
 * Each month changes exactly as if typed (onFillChange() / onCpmChange()):
 * fill is capped at 0-100% and CPM kept inside the bounds, and with Hold
 * budget on the other figure follows so the month stays on budget.
 */
let shiftSnapshot: { fill: number[]; cpm: (number | null)[]; effectiveCpm: number[]; dirty: boolean } | null = null;

/** How far the slider reaches either way: all of the fill scale, or the CPM axis. */
const shiftRange = computed(() =>
	primaryMetric.value === "fill" ? 100 : Math.ceil(maxCpmValue.value)
);

const startPatternShift = () => {
	shiftSnapshot = {
		fill: draftFill.slice(),
		cpm: draftCpm.slice(),
		effectiveCpm: MONTHS.map((_, i) => effectiveCpm(i)),
		dirty: dirty.value,
	};
};

const shiftPattern = (offset: number) => {
	if (!shiftSnapshot) startPatternShift();
	const snapshot = shiftSnapshot!;

	snapshot.fill.forEach((fill, i) => (draftFill[i] = fill));
	snapshot.cpm.forEach((cpm, i) => (draftCpm[i] = cpm));

	if (offset === 0) {
		dirty.value = snapshot.dirty;

		return;
	}

	MONTHS.forEach((_, i) => {
		if (primaryMetric.value === "fill") {
			onFillChange(i, Math.round((snapshot.fill[i] + offset) * 10) / 10);
		} else if (snapshot.effectiveCpm[i] > 0) {
			// A month with nothing to sell has no price to move.
			onCpmChange(i, Math.max(0, Math.round((snapshot.effectiveCpm[i] + offset) * 100) / 100));
		}
	});
};

const endPatternShift = () => {
	shiftSnapshot = null;
	recomputeAxisMax();
};

// A snapshot belongs to the figure it was taken for.
watch(primaryMetric, () => {
	shiftSnapshot = null;
});

const shiftLabel = (offset: number) => {
	const sign = offset > 0 ? "+" : offset < 0 ? "−" : "";

	return primaryMetric.value === "fill"
		? `${sign}${Math.abs(offset).toFixed(1)} pp`
		: `${sign}${formatNumber(Math.abs(offset), "money")}`;
};

onBeforeUnmount(() => {
	window.removeEventListener("pointermove", onDragMove);
	window.removeEventListener("pointerup", onDragEnd);
});

// --- Calculation breakdown (open-inventory book only) ---
const reservedTotal = computed(() =>
	store.reservationBooks
		.filter((b) => b.is_enabled)
		.reduce((sum, b) => sum + store.bookBudget(b), 0)
);

const weightedFillRate = computed(() =>
	props.target.gross_impressions > 0
		? (props.target.sellable_impressions / props.target.gross_impressions) * 100
		: 0
);

// --- Per level below ---
// Both empty for a screen: a screen has nothing below it.
const children = computed(() => props.target.children);

/**
 * This node's own directly-attached screens (see ResolvedNodeDTO.assets) —
 * same source PlanBudgetSplitTab.vue lists, so "Per level below" shows the
 * same set of rows there, not just hierarchy children.
 */
const assets = computed<ResolvedAssetDTO[]>(() => props.target.assets);

// --- Expected revenue gauge (design's header panel, open-inventory book only) ---

/**
 * Effective CPM per month for any resolved node, excluding zero-sellable
 * months (a 0 there isn't a "low CPM", it's undefined) — same derivation
 * PlanHierarchyRow.vue uses for its own min/max CPM columns. Formatted
 * straight to a "low – high" string since every caller (the gauge, the
 * per-level-below table) wants the same display, never the raw numbers.
 */
/** Anything with the twelve-month revenue/sellable pair — a node, a screen, or the current edit target. */
type MonthlyCurve = Pick<
	ResolvedNodeDTO,
	"monthly_revenue" | "monthly_sellable_impressions"
>;

const monthlyCpmRangeOf = (n: MonthlyCurve): string => {
	const values = n.monthly_revenue
		.map((revenue, i) => {
			const sellable = n.monthly_sellable_impressions[i];
			return sellable > 0 ? revenue / (sellable / 1000) : null;
		})
		.filter((value): value is number => value !== null);

	return values.length
		? `${formatNumber(Math.min(...values), "money")} – ${formatNumber(
				Math.max(...values),
				"money"
		  )}`
		: "—";
};

const gaugeStats = computed(() => [
	{ label: "Avg", value: formatNumber(props.target.cpm, "money") },
	{ label: "Min / Max", value: monthlyCpmRangeOf(props.target) },
	{ label: "Avg fill", value: `${weightedFillRate.value.toFixed(1)}%` },
]);

// --- Calculation panel: live, off the current draft (not the last-applied
// node) — sellable impressions, weighted fill rate and year CPM all depend
// on fill/CPM, which the draft can have moved since the last resolve.
// Budget/gross impressions are season-driven only (never fill-driven), so
// those two rows stay off the resolved node, matching BasePriceBookResolver.
const liveSellable = computed(() =>
	MONTHS.reduce(
		(sum, _, i) =>
			sum + props.target.monthly_gross_impressions[i] * (draftFill[i] / 100),
		0
	)
);

const liveWeightedFillRate = computed(() =>
	props.target.gross_impressions > 0
		? (liveSellable.value / props.target.gross_impressions) * 100
		: 0
);

const liveRevenue = computed(() =>
	MONTHS.reduce((sum, _, i) => sum + monthRevenue(i), 0)
);

const liveCpm = computed(() =>
	liveSellable.value > 0 ? liveRevenue.value / (liveSellable.value / 1000) : 0
);

/** A month counts as "manual" in the live draft the same way it does on Apply — see onCpmChange(). */
const liveCpmIsManual = computed(() => draftCpm.some((v) => v !== null));

const reservationCalcFormula =
	"CPM = budget ÷ (reserved impressions ÷ 1000). Reserved inventory is assumed sold, so a fill rate never enters it — this book's whole reservation is what its budget spreads over.";

const calcFormula =
	"Monthly CPM = (budget × monthly budget distribution share) ÷ (impressions ÷ 12 × fill ÷ 1000). Year CPM is the weighted average.";

// --- Per level below (matches the design's own column set) ---
const budgetChip = (revenue: number, budget: number) => {
	const delta = revenue - budget;
	const pct = budget > 0 ? (delta / budget) * 100 : 0;

	if (Math.abs(pct) < 0.25) {
		return {
			bg: "rgba(var(--v-theme-success), 0.12)",
			fg: "rgb(var(--v-theme-success))",
			text: "At budget",
		};
	}
	if (delta > 0) {
		return {
			bg: "rgba(var(--v-theme-info), 0.12)",
			fg: "rgb(var(--v-theme-info))",
			text: `+${formatNumberForScale("money", delta)} · +${pct.toFixed(1)}%`,
		};
	}
	return {
		bg: "rgba(var(--v-theme-error), 0.12)",
		fg: "rgb(var(--v-theme-error))",
		text: `${formatNumberForScale("money", delta)} · ${pct.toFixed(1)}%`,
	};
};

const buildChildRow = (child: ResolvedNodeDTO) => ({
	id: child.hierarchy_node_id,
	name: child.name,
	isAsset: false,
	cpm: formatNumber(child.cpm, "money"),
	cpmColor: child.manual_cpm_own ? "rgb(var(--v-theme-warning))" : "inherit",
	cpmRange: monthlyCpmRangeOf(child),
	revenue: formatNumberForScale("money", child.revenue),
	chip: budgetChip(child.revenue, child.allotted_budget),
});

/**
 * Identical to buildChildRow() in everything but the id space, because a
 * screen is now priced exactly as a node is — its own twelve-month curve,
 * its own revenue, its own manual CPM to highlight. It used to copy the
 * node's range and never highlight, back when the resolver gave every screen
 * the node's uniform rate and only annual totals.
 */
const buildAssetRow = (asset: ResolvedAssetDTO) => ({
	id: asset.asset_id,
	name: asset.name,
	isAsset: true,
	cpm: formatNumber(asset.cpm, "money"),
	cpmColor: asset.manual_cpm_own ? "rgb(var(--v-theme-warning))" : "inherit",
	cpmRange: monthlyCpmRangeOf(asset),
	revenue: formatNumberForScale("money", asset.revenue),
	chip: budgetChip(asset.revenue, asset.budget),
});

/**
 * Cheap combined list (no formatting/range math yet) — a node can carry
 * hundreds of directly-attached screens, and doing the full per-row
 * work (monthlyCpmRangeOf, chip math, string formatting) for every one of
 * them on every render is what made the panel choke the browser. Only the
 * currently visible page actually gets built into full rows below.
 */
const perLevelSource = computed<
	(
		| { kind: "child"; data: ResolvedNodeDTO }
		| { kind: "asset"; data: ResolvedAssetDTO }
	)[]
>(() => [
	...children.value.map((data) => ({ kind: "child" as const, data })),
	...assets.value.map((data) => ({ kind: "asset" as const, data })),
]);

const PER_LEVEL_PAGE_SIZE = 25;
const perLevelPage = ref(0);

const perLevelPageCount = computed(() =>
	Math.max(1, Math.ceil(perLevelSource.value.length / PER_LEVEL_PAGE_SIZE))
);

// Selecting a different node (or the book) always starts back on page 1 —
// an old page index from a bigger list could otherwise land past the end.
watch(
	// Keyed on kind too: a node id and an asset id are separate spaces and
	// can collide, and switching between them must re-seed.
	() => [props.target.kind, props.target.id, store.selectedBookUuid],
	() => (perLevelPage.value = 0)
);

/**
 * Drills into whichever row was clicked — a child node, or one of this
 * node's own screens. Only meaningful while a node is the target; a screen
 * has no rows below it, so this list is empty there.
 */
const onLevelRowClick = (row: { id: number; isAsset: boolean }) => {
	if (row.isAsset) {
		store.selectAsset(props.target.id, row.id);
		return;
	}

	store.selectNode(row.id);
};

const perLevelRows = computed(() => {
	const start = perLevelPage.value * PER_LEVEL_PAGE_SIZE;

	return perLevelSource.value
		.slice(start, start + PER_LEVEL_PAGE_SIZE)
		.map((item) =>
			item.kind === "child"
				? buildChildRow(item.data)
				: buildAssetRow(item.data)
		);
});
</script>

<template>
	<div v-if="isReservationBook">
		<div class="text-body-2 text-medium-emphasis mb-3" style="max-width: 640px">
			This book is fixed: its budget and its reserved impressions are both
			known, so CPM per month is an outcome, not an input. Fill rate doesn't
			apply — reserved inventory is assumed sold.
		</div>

		<v-table density="comfortable">
			<thead>
				<tr>
					<th>Month</th>
					<th class="text-right">Reserved impr.</th>
					<th class="text-right">Index</th>
					<th class="text-right">Budget</th>
					<th class="text-right">CPM</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(monthLabel, i) in MONTHS" :key="monthLabel">
					<td>{{ monthLabel }}</td>
					<td class="text-right">
						{{
							formatNumberForScale(
								"number_no_fraction",
								target.monthly_gross_impressions[i]
							)
						}}
					</td>
					<td class="text-right">
						{{ target.monthly_season_shares[i].toFixed(1) }}%
					</td>
					<td class="text-right">
						{{ formatNumberForScale("money", target.monthly_budget[i]) }}
					</td>
					<td class="text-right font-weight-bold">
						{{ formatNumber(readOnlyMonthlyCpm[i], "money") }}
					</td>
				</tr>
			</tbody>
			<tfoot>
				<tr class="font-weight-bold">
					<td>{{ target.name }} total</td>
					<td class="text-right">
						{{
							formatNumberForScale(
								"number_no_fraction",
								target.gross_impressions
							)
						}}
					</td>
					<td></td>
					<td class="text-right">
						{{ formatNumberForScale("money", target.budget) }}
					</td>
					<td class="text-right">{{ formatNumber(target.cpm, "money") }}</td>
				</tr>
			</tfoot>
		</v-table>
	</div>

	<div v-else>
		<div class="d-flex flex-wrap justify-space-between align-center ga-4 mb-3">
			<plan-month-breadcrumb
				label="CPM & Fill"
				:target-name="target.name"
				:year="store.plan?.year"
				:month="calendarMonth"
				@back="calendarMonth = null"
			/>

			<div class="d-flex align-center ga-3 flex-wrap">
				<div class="mode-toggle">
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': primaryMetric === 'cpm' }"
						@click="setPrimaryMetric('cpm')"
					>
						CPM
					</button>
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': primaryMetric === 'fill' }"
						@click="setPrimaryMetric('fill')"
					>
						Fill rate
					</button>
				</div>

				<div class="mode-toggle">
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': holdBudget }"
						@click="setHoldBudget(true)"
					>
						Hold budget
					</button>
					<button
						class="mode-toggle-option"
						:class="{ 'mode-toggle-option--active': !holdBudget }"
						@click="setHoldBudget(false)"
					>
						Off
					</button>
				</div>

				<v-btn
					v-if="calendarMonth !== null && calendarRef?.ownsMonth"
					size="small"
					variant="tonal"
					color="error"
					prepend-icon="fa-light fa-arrow-rotate-left"
					@click="calendarRef?.resetToInherited()"
				>
					Reset to inherited
				</v-btn>
				<v-btn
					v-if="canReset && calendarMonth === null"
					size="small"
					variant="tonal"
					color="error"
					prepend-icon="fa-light fa-arrow-rotate-left"
					@click="resetToInherited"
				>
					Reset to inherited
				</v-btn>
			</div>
		</div>

		<plan-fill-cpm-calendar
			v-if="calendarMonth !== null"
			ref="calendarRef"
			v-model:month="calendarMonth"
			:target="target"
			:hold="holdBudget"
			:metric="primaryMetric"
			@applied="seedFromDraft()"
		/>

		<template v-else>

		<plan-revenue-gauge
			class="mb-4"
			:revenue="target.revenue"
			:budget="target.budget"
			:title="`Expected revenue · ${target.name}`"
			tracks-pending
			:pending="dirty"
			:context="`against budget ${formatNumberForScale('money', target.budget)} · ${store.selectedBook?.name ?? ''}`"
			:stats="gaugeStats"
		/>

		<div class="d-flex align-center ga-4 mb-2">
			<div class="d-flex align-center ga-2 text-caption text-medium-emphasis">
				<span class="legend-square" :style="{ background: FILL_COLOR }" />
				Fill rate ({{ primaryMetric === "fill" ? "bars" : "line" }})
			</div>
			<div class="d-flex align-center ga-2 text-caption text-medium-emphasis">
				<span class="legend-dash" :style="{ background: CPM_COLOR }" />
				CPM ({{ primaryMetric === "cpm" ? "bars" : "line" }})
			</div>
		</div>

		<div class="d-flex">
		<div class="flex-grow-1" style="overflow-x: auto; min-width: 0">
			<div class="fillcpm-chart-wrap">
				<!--
					Drawn across the plot rather than per column: a bound is one
					number for the whole year, and a line is what makes a clamped
					month legible as "it hit the ceiling" rather than "it is oddly
					flat".
				-->
				<template v-if="showBoundLines">
					<div
						v-if="draftMaxCpm !== null"
						class="fillcpm-bound-line fillcpm-bound-line--max"
						:style="{ top: boundTop(draftMaxCpm) }"
						:title="`Ceiling ${formatNumber(draftMaxCpm, 'money')}`"
					/>
					<div
						v-if="draftMinCpm !== null"
						class="fillcpm-bound-line fillcpm-bound-line--min"
						:style="{ top: boundTop(draftMinCpm) }"
						:title="`Floor ${formatNumber(draftMinCpm, 'money')}`"
					/>
				</template>
				<div class="fillcpm-grid">
					<div
						v-for="(monthLabel, i) in MONTHS"
						:key="monthLabel"
						class="fillcpm-col"
					>
						<div
							class="fillcpm-bar-track"
							:style="{ height: chartHeight + 'px' }"
							@pointerdown="onBarPointerDown(i, $event)"
						>
							<div
								class="fillcpm-bar-fill"
								:style="{ height: barHeight(i) + 'px', background: barColor }"
							/>
							<div class="fillcpm-bar-value">
								{{ barValueLabel(i) }}
							</div>
						</div>
					</div>
				</div>

				<svg
					class="fillcpm-line-overlay"
					:style="{ height: chartHeight + 'px' }"
					viewBox="0 0 12 100"
					preserveAspectRatio="none"
				>
					<polyline
						:points="linePoints"
						fill="none"
						:stroke="lineColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						vector-effect="non-scaling-stroke"
					/>
				</svg>
			</div>

			<div class="fillcpm-grid mt-2">
				<div
					v-for="(monthLabel, i) in MONTHS"
					:key="monthLabel"
					class="fillcpm-col"
				>
					<div class="fillcpm-input-box">
						<input
							type="number"
							step="0.1"
							:value="draftFill[i].toFixed(1)"
							@change="
								onFillInput(
									i,
									Number(($event.target as HTMLInputElement).value)
								)
							"
						/>
						<span class="fillcpm-input-affix">%</span>
					</div>

					<div
						class="fillcpm-input-box"
						:class="{
							'fillcpm-input-box--bound': isAtBound(i),
							'fillcpm-input-box--manual': draftCpm[i] !== null,
							'fillcpm-input-box--derived': draftCpm[i] === null,
						}"
					>
						<span class="fillcpm-input-affix">€</span>
						<input
							type="number"
							step="0.01"
							:value="(draftCpm[i] ?? effectiveCpm(i)).toFixed(2)"
							@change="onCpmInput(i, ($event.target as HTMLInputElement).value)"
						/>
					</div>

					<div class="fillcpm-revenue">
						{{ formatNumber(monthRevenue(i), "money") }}
					</div>
					<div class="fillcpm-delta" :class="monthDelta(i).colorClass">
						{{ monthDelta(i).label }}
					</div>

					<!-- Opens the month by day — disabled while the months have unapplied changes, see openMonth(). -->
					<plan-month-label
						:label="monthLabel"
						:name="MONTH_NAMES[i]"
						:marked="monthsWithDayPricing.has(i)"
						marked-hint="its days are priced by hand"
						:disabled-reason="
							dirty ? 'Apply or cancel your changes to the months first' : null
						"
						@open="openMonth(i)"
					/>
				</div>
			</div>
		</div>
			<!-- Moves the whole bar pattern up or down together — see shiftPattern(). -->
			<plan-pattern-nudge
				:range="shiftRange"
				:step="primaryMetric === 'fill' ? 0.1 : 0.01"
				:color="primaryMetric === 'fill' ? FILL_COLOR : CPM_COLOR"
				:height="chartHeight"
				:format="shiftLabel"
				:title="`Move the whole ${primaryMetric === 'fill' ? 'fill rate' : 'CPM'} pattern up or down`"
				:button-step="primaryMetric === 'fill' ? 0.1 : 0.01"
				@start="startPatternShift"
				@shift="shiftPattern"
				@end="endPatternShift"
			/>
		</div>

		<!--
			Assists on the left, the price bounds on the right — they sit with
			the twelve inputs they constrain rather than up in the header beside
			the display toggles, which read as view settings and gave no hint
			that this one rewrites values.
		-->
		<div class="d-flex ga-4 flex-wrap align-center justify-space-between my-4">
			<div class="d-flex align-center">
			<v-tooltip :text="solveHint" location="bottom">
				<!-- On a wrapper, so the tooltip still explains a disabled button. -->
				<template v-slot:activator="{ props: tooltip }">
					<span v-bind="tooltip">
						<v-btn
							size="small"
							variant="tonal"
							:disabled="!solveWouldChange"
							@click="solveToBudget"
						>
							Solve to budget
						</v-btn>
					</span>
				</template>
			</v-tooltip>
			<v-tooltip
				v-if="primaryMetric === 'cpm'"
				text="Sets every month's fill and price so the year averages a CPM you choose, with each month still on budget."
				location="bottom"
			>
				<template v-slot:activator="{ props: tooltip }">
					<v-btn
						v-bind="tooltip"
						size="small"
						variant="tonal"
						class="ml-2"
						:disabled="boundsInvalid"
						@click="averageCpmOpen = true"
					>
						Define average CPM
					</v-btn>
				</template>
			</v-tooltip>
			<v-tooltip
				v-if="primaryMetric === 'cpm'"
				text="Sets every month's CPM after what this target was priced at in earlier years."
				location="bottom"
			>
				<template v-slot:activator="{ props: tooltip }">
					<v-btn
						v-bind="tooltip"
						size="small"
						variant="tonal"
						class="ml-2"
						prepend-icon="fa-light fa-clock-rotate-left"
						:disabled="boundsInvalid"
						@click="previousYearsCpmOpen = true"
					>
						Derive CPM from previous years
					</v-btn>
				</template>
			</v-tooltip>
			<plan-previous-years-cpm-dialog
				v-model:visible="previousYearsCpmOpen"
				:target-name="target.name"
				@apply="applyPreviousYearsCpm"
			/>
			<v-tooltip
				v-if="primaryMetric === 'fill'"
				text="Sets every month's fill rate after how much of this target sold in earlier years."
				location="bottom"
			>
				<template v-slot:activator="{ props: tooltip }">
					<v-btn
						v-bind="tooltip"
						size="small"
						variant="tonal"
						class="ml-2"
						prepend-icon="fa-light fa-clock-rotate-left"
						@click="previousYearsFillOpen = true"
					>
						Derive fill rate from previous years
					</v-btn>
				</template>
			</v-tooltip>
			<plan-previous-years-fill-dialog
				v-model:visible="previousYearsFillOpen"
				:target-name="target.name"
				@apply="applyPreviousYearsFill"
			/>
			<plan-average-cpm-dialog
				v-model:visible="averageCpmOpen"
				:months="MONTHS"
				:gross="target.monthly_gross_impressions"
				:budget="target.monthly_budget"
				:fill="draftFill"
				:cpm="MONTHS.map((_, i) => effectiveCpm(i))"
				:bounds="{ min: draftMinCpm, max: draftMaxCpm }"
				:current-average="target.cpm"
				:evaluate="evaluateAverageCpm"
				@apply="applyAverageCpm"
			/>
			</div>

			<div class="d-flex align-center ga-2">
				<span class="text-caption text-medium-emphasis">Clamp CPM between</span>
				<div
					class="bounds-input-box"
					:class="{ 'bounds-input-box--invalid': boundsInvalid }"
				>
					<span class="bounds-input-affix">€</span>
					<input
						type="number"
						step="0.25"
						min="0"
						placeholder="min"
						:value="draftMinCpm"
						@change="
							setBound(
								'min',
								($event.target as HTMLInputElement).value === ''
									? null
									: Number(($event.target as HTMLInputElement).value)
							)
						"
					/>
				</div>
				<span class="text-caption text-medium-emphasis">and</span>
				<div
					class="bounds-input-box"
					:class="{ 'bounds-input-box--invalid': boundsInvalid }"
				>
					<span class="bounds-input-affix">€</span>
					<input
						type="number"
						step="0.25"
						min="0"
						placeholder="max"
						:value="draftMaxCpm"
						@change="
							setBound(
								'max',
								($event.target as HTMLInputElement).value === ''
									? null
									: Number(($event.target as HTMLInputElement).value)
							)
						"
					/>
				</div>
			</div>
		</div>

		<div v-if="boundsInvalid" class="text-caption text-error text-right mb-2">
			<i class="fa-light fa-triangle-exclamation mr-1" />
			The floor is above the ceiling — no price could satisfy both.
		</div>
		<div
			v-else-if="cpmOutOfBounds"
			class="text-caption text-error text-right mb-2"
		>
			<i class="fa-light fa-triangle-exclamation mr-1" />
			A hand-set CPM here sits outside these bounds. It is priced at the bound
			regardless — clear it, or widen the bounds, to say which you meant.
		</div>
		<div
			v-else-if="draftMinCpm !== null || draftMaxCpm !== null"
			class="text-caption text-medium-emphasis text-right mb-2"
		>
			Prices are held inside these bounds, so a month that would have priced
			outside them earns more or less than its budget asked for. Solve to budget
			moves fill to bring them back.
		</div>

		<!--
			Said separately from the bounds note above, and not as another branch
			of it: this one survives the solve. Every other message here
			describes something the solve fixes.
		-->
		<div
			v-if="!boundsInvalid && unreachableMonths > 0"
			class="text-caption text-warning text-right mb-2"
		>
			<i class="fa-light fa-triangle-exclamation mr-1" />
			{{ unreachableMonths }}
			{{ unreachableMonths === 1 ? "month" : "months" }} cannot reach budget at
			this ceiling — even every impression sold would earn less. Solve to budget
			takes them to full sell-through and leaves the shortfall.
		</div>

		<div class="d-flex justify-end ga-2">
			<v-btn color="error" variant="text" @click="cancel">Cancel</v-btn>
			<v-btn variant="tonal" color="primary" :disabled="!dirty" @click="apply">
				Apply fill &amp; CPM
			</v-btn>
		</div>
		</template>
	</div>

	<!--
		Shared by both books: the same two questions are worth answering either
		way — how this level's price was arrived at, and how it breaks down one
		level further. Only the calculation's rows differ, since a reservation
		book's inventory is what it reserved rather than what the reservations
		left.
	-->
	<div class="breakdown-pair mt-6 mb-4">
		<v-card variant="outlined" class="pa-4">
			<div class="text-caption text-uppercase text-medium-emphasis mb-2">
				Calculation
			</div>
			<template v-if="isReservationBook">
				<div class="calc-row">
					<span>Target</span>
					<span>{{
						formatNumberForScale("money", store.plan?.target ?? 0)
					}}</span>
				</div>
				<div class="calc-row">
					<span>Carve-out for this book</span>
					<span
						>{{
							(store.selectedBook?.budget_percentage ?? 0).toFixed(1)
						}}%</span
					>
				</div>
				<div class="calc-row font-weight-bold">
					<span>Budget at this level</span>
					<span>{{ formatNumberForScale("money", target.budget) }}</span>
				</div>
				<div class="calc-row">
					<span>Reserved impressions</span>
					<span>{{
						formatNumberForScale("number_no_fraction", target.gross_impressions)
					}}</span>
				</div>
				<div class="calc-row font-weight-bold">
					<span>Sellable impressions</span>
					<span>{{
						formatNumberForScale(
							"number_no_fraction",
							target.sellable_impressions
						)
					}}</span>
				</div>
				<div class="calc-row font-weight-bold">
					<span>Derived CPM (year)</span>
					<span>{{ formatNumber(target.cpm, "money") }}</span>
				</div>
			</template>
			<template v-else>
				<div class="calc-row">
					<span>Target</span>
					<span>{{
						formatNumberForScale("money", store.plan?.target ?? 0)
					}}</span>
				</div>
				<div class="calc-row">
					<span>− Reservation books</span>
					<span>−{{ formatNumberForScale("money", reservedTotal) }}</span>
				</div>
				<div class="calc-row">
					<span>Open budget</span>
					<span>{{
						formatNumberForScale(
							"money",
							store.bookBudget(store.openInventoryBook!)
						)
					}}</span>
				</div>
				<div class="calc-row font-weight-bold">
					<span>Budget at this level</span>
					<span>{{ formatNumberForScale("money", target.budget) }}</span>
				</div>
				<div class="calc-row">
					<span>Open impressions</span>
					<span>{{
						formatNumberForScale("number_no_fraction", target.gross_impressions)
					}}</span>
				</div>
				<div class="calc-row">
					<span>Weighted fill rate</span>
					<span>{{ liveWeightedFillRate.toFixed(1) }}%</span>
				</div>
				<div class="calc-row font-weight-bold">
					<span>Sellable impressions</span>
					<span>{{
						formatNumberForScale("number_no_fraction", liveSellable)
					}}</span>
				</div>
				<div class="calc-row font-weight-bold">
					<span>{{
						liveCpmIsManual ? "Manual CPM (year)" : "Derived CPM (year)"
					}}</span>
					<span>{{ formatNumber(liveCpm, "money") }}</span>
				</div>
			</template>
			<div
				class="text-caption text-medium-emphasis mt-2"
				style="line-height: 1.5"
			>
				{{ isReservationBook ? reservationCalcFormula : calcFormula }}
			</div>
		</v-card>

		<v-card v-if="perLevelSource.length" variant="outlined" class="pa-0">
			<div class="d-flex align-center justify-space-between pa-4 pb-2">
				<span class="text-caption text-uppercase text-medium-emphasis">
					Per level below
				</span>
				<span
					v-if="perLevelPageCount > 1"
					class="text-caption text-medium-emphasis"
				>
					{{ perLevelPage * PER_LEVEL_PAGE_SIZE + 1 }}–{{
						Math.min(
							(perLevelPage + 1) * PER_LEVEL_PAGE_SIZE,
							perLevelSource.length
						)
					}}
					of {{ perLevelSource.length }}
				</span>
			</div>
			<div
				class="fillcpm-level-row fillcpm-level-row--head text-caption text-uppercase text-medium-emphasis"
			>
				<span>Level</span>
				<span class="text-right">CPM year</span>
				<span class="text-right">Min / max</span>
				<span class="text-right">Revenue</span>
				<span class="text-right">vs budget</span>
			</div>
			<div
				v-for="row in perLevelRows"
				:key="row.id"
				class="fillcpm-level-row"
				:class="{ 'fillcpm-level-row--asset': row.isAsset }"
				@click="onLevelRowClick(row)"
			>
				<span class="text-truncate fillcpm-level-name">
					<!-- In a span, not on the <i>: Font Awesome replaces the <i>, see PlanHierarchyRow.vue. -->
					<span v-if="row.isAsset"
						><i class="fa-regular fa-display fillcpm-level-asset-icon"
					/></span>
					{{ row.name }}
				</span>
				<span
					class="text-right font-weight-bold"
					:style="{ color: row.cpmColor }"
					>{{ row.cpm }}</span
				>
				<span class="text-right text-medium-emphasis fillcpm-level-range">{{
					row.cpmRange
				}}</span>
				<span class="text-right text-medium-emphasis">{{ row.revenue }}</span>
				<span
					class="fillcpm-level-chip"
					:style="{ background: row.chip.bg, color: row.chip.fg }"
					>{{ row.chip.text }}</span
				>
			</div>

			<div
				v-if="perLevelPageCount > 1"
				class="d-flex align-center justify-space-between pa-3"
			>
				<v-btn
					size="small"
					variant="text"
					:disabled="perLevelPage === 0"
					prepend-icon="fa-light fa-chevron-left"
					@click="perLevelPage--"
				>
					Previous
				</v-btn>
				<span class="text-caption text-medium-emphasis">
					Page {{ perLevelPage + 1 }} of {{ perLevelPageCount }}
				</span>
				<v-btn
					size="small"
					variant="text"
					:disabled="perLevelPage >= perLevelPageCount - 1"
					append-icon="fa-light fa-chevron-right"
					@click="perLevelPage++"
				>
					Next
				</v-btn>
			</div>
		</v-card>
	</div>
</template>

<style scoped lang="scss">
.mode-toggle {
	display: flex;
	gap: 2px;
	padding: 3px;
	border-radius: 7px;
	background: rgba(var(--v-theme-on-surface), 0.06);
}

.mode-toggle-option {
	padding: 4px 10px;
	border-radius: 5px;
	font-size: 12px;
	font-weight: 600;
	cursor: pointer;
	border: 0;
	background: transparent;
	color: inherit;
}

.mode-toggle-option--active {
	background: rgb(var(--v-theme-surface));
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

.fillcpm-grid {
	display: grid;
	grid-template-columns: repeat(12, minmax(64px, 1fr));
	gap: 6px;
	min-width: 860px;
}

.bounds-input-box {
	display: flex;
	align-items: center;
	gap: 2px;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	padding: 3px 7px;
}

.bounds-input-box--invalid {
	border-color: rgb(var(--v-theme-error));
}

.bounds-input-box input {
	width: 52px;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12.5px;
	font-weight: 600;
	text-align: right;
	font-variant-numeric: tabular-nums;
}

.bounds-input-box input::-webkit-outer-spin-button,
.bounds-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.bounds-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.fillcpm-bound-line {
	position: absolute;
	left: 0;
	right: 0;
	height: 0;
	border-top: 1.5px dashed rgb(var(--v-theme-error));
	pointer-events: none;
	z-index: 2;
}

.fillcpm-bound-line--min {
	border-top-style: dotted;
}

.fillcpm-input-box--bound {
	border-color: rgb(var(--v-theme-error));
}

.fillcpm-chart-wrap {
	position: relative;
}

.fillcpm-line-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	min-width: 860px;
	pointer-events: none;
}

.fillcpm-col {
	display: flex;
	flex-direction: column;
}

.legend-square {
	width: 8px;
	height: 8px;
	border-radius: 2px;
	flex: 0 0 auto;
}

.legend-dash {
	width: 12px;
	height: 2px;
	border-radius: 1px;
	flex: 0 0 auto;
}

.fillcpm-bar-track {
	position: relative;
	border-radius: 8px;
	background: rgba(var(--v-theme-on-surface), 0.04);
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	cursor: ns-resize;
	user-select: none;
	overflow: hidden;
}

.fillcpm-bar-fill {
	position: absolute;
	left: 3px;
	right: 3px;
	bottom: 0;
	border-radius: 4px 4px 0 0;
	background: v-bind(FILL_COLOR);
	transition: height 0.1s ease;
}

.fillcpm-bar-value {
	position: absolute;
	left: 0;
	right: 0;
	top: 6px;
	text-align: center;
	font-size: 11px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	// In the normal ink: the bar's own colour is too light to read as text.
	color: rgb(var(--v-theme-on-surface));
}

.fillcpm-input-box {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 8px;
	height: 28px;
	border: 1.5px solid rgba(var(--v-theme-on-surface), 0.25);
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

.fillcpm-input-box--manual {
	border-color: v-bind(CPM_COLOR);
}

.fillcpm-input-box--derived input {
	color: rgba(var(--v-theme-on-surface), 0.6);
	font-style: italic;
}

.fillcpm-input-box input {
	width: 100%;
	min-width: 0;
	border: 0;
	outline: none;
	background: transparent;
	color: inherit;
	font: inherit;
	font-size: 12px;
	font-weight: 600;
	text-align: center;
	font-variant-numeric: tabular-nums;
}

.fillcpm-input-box input::-webkit-outer-spin-button,
.fillcpm-input-box input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.fillcpm-input-box input[type="number"] {
	-moz-appearance: textfield;
}

.fillcpm-input-affix {
	font-size: 11px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	padding: 0 4px;
}

.fillcpm-revenue {
	margin-top: 4px;
	text-align: center;
	font-size: 10px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	font-variant-numeric: tabular-nums;
}

.fillcpm-delta {
	text-align: center;
	font-size: 11px;
	font-weight: 700;
	font-variant-numeric: tabular-nums;
}

// Two equal columns that never stack: the calculation and the level below it
// answer the same question from two directions, so reading one above the other
// loses the comparison. The calculation takes the full width only when there
// is no level below — on a screen, or a node with nothing under it — rather
// than sitting in half a row next to a gap.
.breakdown-pair {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	align-items: start;
}

.breakdown-pair > *:only-child {
	grid-column: 1 / -1;
}

.calc-row {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	padding: 4px 0;
	font-size: 13px;
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
}

.calc-row:last-child {
	border-bottom: 0;
}

/*
 * Each row is its own independent grid (not a shared table), so any track
 * sized off content (fr, minmax(..,auto)) resolves differently per row —
 * the header ends up with different column widths than the data rows below
 * it. Every column but the name is a fixed px width for that reason; only
 * "Level" is 1fr, and since every other track is fixed (content-independent)
 * the remaining space it absorbs is identical across every row.
 */
.fillcpm-level-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 84px 130px 80px 150px;
	gap: 10px;
	align-items: center;
	padding: 10px 16px;
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
	min-width: 0;
	font-size: 13px;
	cursor: pointer;
}

.fillcpm-level-row > span:not(.fillcpm-level-chip) {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.fillcpm-level-row:last-child {
	border-bottom: 0;
}

.fillcpm-level-row:hover {
	background: rgba(var(--v-theme-on-surface), 0.04);
}

.fillcpm-level-row--head {
	cursor: default;
	font-size: 10px;
	letter-spacing: 0.04em;
	background: rgba(var(--v-theme-on-surface), 0.02);
}

.fillcpm-level-row--head:hover {
	background: rgba(var(--v-theme-on-surface), 0.02);
}

.fillcpm-level-row--asset {
	cursor: default;
}

.fillcpm-level-row--asset:hover {
	background: transparent;
}

.fillcpm-level-asset-icon {
	font-size: 10px;
	color: rgba(var(--v-theme-on-surface), 0.4);
	margin-right: 4px;
}

.fillcpm-level-name {
	min-width: 0;
}

.fillcpm-level-range {
	font-size: 11.5px;
	font-variant-numeric: tabular-nums;
}

.fillcpm-level-chip {
	display: inline-flex;
	justify-self: end;
	border-radius: 12px;
	padding: 3px 9px;
	font-size: 11px;
	font-weight: 600;
	white-space: nowrap;
}
</style>
